import React, { Component } from 'react'
import { IWindowOptions, Window } from '.'

// Matches the interface in syrup.tab.tsx
interface EnhancedPersistentMapEntry {
    key: string
    value: string
    name: string
    category: string
    type?: 'text' | 'number'
    validation?: {
        min?: number
        max?: number
    }
    changed?: boolean
}

export async function askPersistentMap(entries: EnhancedPersistentMapEntry[], options?: IWindowOptions): Promise<any> {
    console.log('entries', entries)

    let formRef: PersistentMapForm | null = null
    return new Window().fire({
        ...options,
        body: <PersistentMapForm ref={ref => (formRef = ref)} initialEntries={entries} />,
        showCancelButton: true,
        showLoaderOnConfirm: true,
        allowEnterKey: false, // Disable enter key to prevent accidental submission during typing
        allowOutsideClick: false,
        preConfirm: () => {
            if (formRef && formRef.hasValidationErrors()) {
                // Prevent closing if there are errors
                new Window().fire({
                    title: 'שגיאות באימות: נא לתקן את השדות המסומנים.',
                })
                return false
            }
            return formRef?.state.entries
        },
    })
}

type Props = {
    initialEntries: EnhancedPersistentMapEntry[]
}

type State = {
    entries: EnhancedPersistentMapEntry[]
    validationErrors: Record<string, string | null> // Store error messages per entry key
}

class PersistentMapForm extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            entries: JSON.parse(JSON.stringify(props.initialEntries)), // Deep copy to avoid mutating original
            validationErrors: {},
        }
    }

    hasValidationErrors = (): boolean => {
        return Object.values(this.state.validationErrors).some(error => error !== null)
    }

    validateEntry = (key: string, value: string, entryType?: 'text' | 'number', validationRules?: { min?: number; max?: number }): string | null => {
        if (entryType === 'number') {
            const numValue = parseFloat(value)
            if (isNaN(numValue)) return 'יש להזין ערך מספרי.'
            if (validationRules?.min !== undefined && numValue < validationRules.min) {
                return `הערך חייב להיות גדול או שווה ל-${validationRules.min}.`
            }
            if (validationRules?.max !== undefined && numValue > validationRules.max) {
                return `הערך חייב להיות קטן או שווה ל-${validationRules.max}.`
            }
        }
        // Add more validation types here as needed (e.g., required, regex)
        return null // No error
    }

    handleChange = (key: string, newValue: string) => {
        const entryIndex = this.state.entries.findIndex(e => e.key === key)
        if (entryIndex === -1) return

        const entry = this.state.entries[entryIndex]
        const validationError = this.validateEntry(key, newValue, entry.type, entry.validation)

        this.setState(prevState => ({
            entries: prevState.entries.map(e => (e.key === key ? { ...e, value: newValue, changed: true } : e)),
            validationErrors: {
                ...prevState.validationErrors,
                [key]: validationError,
            },
        }))
    }

    render() {
        return (
            <div className="max-h-[70vh] overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
                {this.renderEntries()}
            </div>
        )
    }

    renderEntries() {
        const { entries, validationErrors } = this.state

        const groupedByCategory = entries.reduce((acc, entry) => {
            const category = entry.category || 'Uncategorized'
            if (!acc[category]) {
                acc[category] = []
            }
            acc[category].push(entry)
            return acc
        }, {} as Record<string, EnhancedPersistentMapEntry[]>)

        const predefinedCategoryOrder = [
            'System Configuration',
            'Relsol Configuration',
            'Flavor IO Pins',
            'Pour Pulse Defaults - Water',
            'Pour Pulse Defaults - Soda',
            'Pump Calibration Defaults',
            'נתוני כיול',
            'Uncategorized',
        ]

        const actualCategories = Object.keys(groupedByCategory)

        actualCategories.sort((a, b) => {
            const indexA = predefinedCategoryOrder.indexOf(a)
            const indexB = predefinedCategoryOrder.indexOf(b)

            if (indexA !== -1 && indexB !== -1) return indexA - indexB
            if (indexA !== -1) return -1
            if (indexB !== -1) return 1
            return a.localeCompare(b)
        })

        return actualCategories.map(categoryName => {
            const categoryEntries = groupedByCategory[categoryName]
            if (!categoryEntries || categoryEntries.length === 0) return null

            return (
                <div key={categoryName} className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400 border-b border-gray-200 dark:border-gray-700 pb-2 mb-3">
                        {categoryName}
                    </h3>
                    {categoryEntries.map(entry => {
                        const error = validationErrors[entry.key]
                        return (
                            <div key={entry.key} className="mb-3 text-start">
                                <label htmlFor={entry.key} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {entry.name} <span className="text-xs text-gray-500 dark:text-gray-400">({entry.key})</span>
                                </label>
                                <input
                                    id={entry.key}
                                    className={`pretty-input w-full text-sm p-2 mt-1 border ${
                                        error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                    } rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white`}
                                    type={entry.type === 'number' ? 'number' : 'text'}
                                    value={entry.value}
                                    onChange={e => this.handleChange(entry.key, e.target.value)}
                                    min={entry.type === 'number' && entry.validation?.min !== undefined ? entry.validation.min : undefined}
                                    max={entry.type === 'number' && entry.validation?.max !== undefined ? entry.validation.max : undefined}
                                    step={entry.type === 'number' ? 'any' : undefined}
                                />
                                {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
                            </div>
                        )
                    })}
                </div>
            )
        })
    }
}
